import { z } from "zod";

// Module « guides » : documents en étapes ordonnées (procédures, SOP,
// tutoriels) rédigés en markdown. Un guide est un brouillon tant qu'il n'a
// jamais été publié ; chaque publication incrémente sa version et fige une
// copie (guide + étapes) dans les révisions.

export type GuideVisibility = "public" | "members" | "admins";
export type GuideStatus = "draft" | "published";

export interface Guide {
  id: string;
  /** Identifiant lisible dans l'URL (kebab-case), unique. */
  slug: string;
  title: string;
  description: string | null;
  visibility: GuideVisibility;
  status: GuideStatus;
  /** Ordre d'affichage dans la liste. */
  position: number;
  /** 0 tant que le guide n'a jamais été publié. */
  version: number;
  createdAt: number | string;
  updatedAt: number | string;
  createdByUserId: string | null;
}

export interface GuideStep {
  id: string;
  guideId: string;
  position: number;
  title: string;
  bodyMd: string;
  createdAt: number | string;
  updatedAt: number | string;
}

/** Guide accompagné de ses étapes, dans l'ordre. */
export interface GuideWithSteps extends Guide {
  steps: GuideStep[];
}

/** Copie figée au moment d'une publication. */
export interface GuideSnapshot {
  guide: Guide;
  steps: GuideStep[];
}

export interface GuideRevision {
  id: string;
  guideId: string;
  version: number;
  /** `GuideSnapshot` sérialisé. */
  snapshotJson: string;
  createdAt: number | string;
  createdByUserId: string | null;
}

export const GUIDE_VISIBILITIES: GuideVisibility[] = ["public", "members", "admins"];
export const GUIDE_STATUSES: GuideStatus[] = ["draft", "published"];

export const guideVisibilitySchema = z.enum(["public", "members", "admins"]);
export const guideStatusSchema = z.enum(["draft", "published"]);

/** Slug : minuscules, chiffres et tirets simples, sans tiret aux extrémités. */
export const guideSlugSchema = z
  .string()
  .min(1)
  .max(120)
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "slug en minuscules, chiffres et tirets");

/**
 * Dérive un slug d'un titre : accents retirés, tout ce qui n'est pas
 * alphanumérique devient un tiret. « Procédure d'ouverture » → « procedure-d-ouverture ».
 */
export function slugify(title: string): string {
  return title
    .normalize("NFD")
    .replace(/\p{M}/gu, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 120)
    .replace(/-+$/g, "");
}

export const createGuideSchema = z.object({
  title: z.string().trim().min(1).max(160),
  /** Dérivé du titre s'il est absent. */
  slug: guideSlugSchema.optional(),
  description: z.string().max(2000).nullable().optional(),
  visibility: guideVisibilitySchema.default("members"),
  position: z.number().int().min(0).optional(),
});
export type CreateGuideInput = z.infer<typeof createGuideSchema>;

export const updateGuideSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  slug: guideSlugSchema.optional(),
  description: z.string().max(2000).nullable().optional(),
  visibility: guideVisibilitySchema.optional(),
  position: z.number().int().min(0).optional(),
});
export type UpdateGuideInput = z.infer<typeof updateGuideSchema>;

export const createStepSchema = z.object({
  title: z.string().trim().min(1).max(160),
  bodyMd: z.string().max(100_000).default(""),
});
export type CreateStepInput = z.infer<typeof createStepSchema>;

export const updateStepSchema = z.object({
  title: z.string().trim().min(1).max(160).optional(),
  bodyMd: z.string().max(100_000).optional(),
});
export type UpdateStepInput = z.infer<typeof updateStepSchema>;

/** Liste complète et ordonnée des identifiants d'étapes du guide. */
export const orderStepsSchema = z.object({
  ids: z.array(z.string().min(1)).min(1),
});
export type OrderStepsInput = z.infer<typeof orderStepsSchema>;

export const GUIDE_VISIBILITY_LABELS: Record<GuideVisibility, string> = {
  public: "Public",
  members: "Membres",
  admins: "Administrateurs",
};

/** Explication courte affichée à côté du choix de visibilité. */
export const GUIDE_VISIBILITY_HINTS: Record<GuideVisibility, string> = {
  public: "Lisible par tous, même sans connexion, via le lien public.",
  members: "Réservé aux comptes connectés.",
  admins: "Réservé aux administrateurs.",
};

export const GUIDE_STATUS_LABELS: Record<GuideStatus, string> = {
  draft: "Brouillon",
  published: "Publié",
};
